'use client';

import {
  autoUpdate,
  flip,
  offset,
  OffsetOptions,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  UseFloatingReturn,
  useInteractions,
} from '@floating-ui/react';
import {
  getClientSideUserAgent,
  isAppWebView,
  isIOSMobile,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from '@vuddy/utils';
import { useMemo, useState } from 'react';

export interface UseDropdownOptions {
  initialOpen?: boolean;
  placement?: Placement;
  offset?: OffsetOptions;
  fallbackPlacements?: Placement[];
  open?: boolean;
  onChangeOpen?: (open: boolean) => void;
  enableSizeMiddleware?: boolean;
}

export interface UseDropdownReturn extends UseFloatingReturn {
  open: boolean;
  setOpen: (open: boolean) => void;
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
}

export const useDropdown = ({
  initialOpen = false,
  open: controlledOpen,
  onChangeOpen: setControlledOpen,
  placement = 'bottom-start',
  offset: offsetOptions,
  fallbackPlacements,
  enableSizeMiddleware = false,
}: UseDropdownOptions): UseDropdownReturn => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const ua = getClientSideUserAgent();
  const isIOS = isIOSMobile(ua) || isAppWebView(ua);

  const floating = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    // iOS에서는 transform 기반 포지셔닝이 터치 히트박스를 어긋나게 하므로
    // top/left 포지셔닝을 사용
    transform: !isIOS,
    middleware: [
      offset(offsetOptions),
      // bottom 영역이 SAFE_AREA_BOTTOM으로 제한되어 최소 높이를 확보하지 못할 때,
      // 완전히 들어맞는 첫 번째 대안(top-start 등)으로 즉시 뒤집도록 설정
      flip({
        fallbackPlacements,
        crossAxis: false,
        fallbackStrategy: 'initialPlacement',
      }),
      shift({ padding: { top: SAFE_AREA_TOP, bottom: SAFE_AREA_BOTTOM } }),
      ...(enableSizeMiddleware
        ? [
            size({
              padding: { top: SAFE_AREA_TOP, bottom: SAFE_AREA_BOTTOM },
              apply({ availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  maxHeight: `${availableHeight}px`,
                  overflowY: 'auto',
                });
              },
            }),
          ]
        : []),
    ],
  });

  const { context } = floating;

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const interactions = useInteractions([click, dismiss]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...floating,
    }),
    [open, setOpen, interactions, floating]
  );
};
